import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Alert,
  Divider,
} from '@mui/material';
import {
  Block as DeactivateIcon,
  Add as AddIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { fetchPromptVersions, createNewVersion, deactivateVersion } from './llm-prompts.service';

const LLMPromptVersions = ({ open, onClose, promptKey, onChanged }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedVersion, setExpandedVersion] = useState(null);
  const [showNewVersion, setShowNewVersion] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [deactivating, setDeactivating] = useState(null);

  const load = useCallback(async () => {
    if (!promptKey) return;
    setLoading(true);
    try {
      const res = await fetchPromptVersions(promptKey);
      setVersions(res?.result || []);
    } catch (e) {
      toast.error('Failed to load versions');
    } finally {
      setLoading(false);
    }
  }, [promptKey]);

  useEffect(() => {
    if (open && promptKey) {
      load();
      setShowNewVersion(false);
      setNewContent('');
      setNewDescription('');
    }
  }, [open, promptKey, load]);

  const handleDeactivate = async (version) => {
    setDeactivating(version);
    try {
      await deactivateVersion(promptKey, version);
      toast.success(`Version ${version} deactivated`);
      load();
      onChanged();
    } catch (e) {
      toast.error(e || 'Failed to deactivate version');
    } finally {
      setDeactivating(null);
    }
  };

  const handleCreateVersion = async () => {
    if (!newContent.trim()) {
      toast.error('Prompt content is required');
      return;
    }
    setCreating(true);
    try {
      await createNewVersion(promptKey, {
        promptContent: newContent,
        description: newDescription || null,
      });
      toast.success('New version created');
      setShowNewVersion(false);
      setNewContent('');
      setNewDescription('');
      load();
      onChanged();
    } catch (e) {
      toast.error(e || 'Failed to create version');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            Version History
            <Typography variant="body2" color="text.secondary" component="div">
              <code>{promptKey}</code>
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setShowNewVersion(!showNewVersion)}
            size="small"
          >
            New Version
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* New Version Form */}
        {showNewVersion && (
          <Box sx={{ mb: 3, p: 2, border: '1px solid #3366FF', borderRadius: 2, bgcolor: '#eef2ff' }}>
            <Typography variant="subtitle2" mb={1}>Create New Version</Typography>
            <TextField
              label="Description (optional)"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 1.5 }}
            />
            <TextField
              label="Prompt Content"
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              multiline
              minRows={6}
              maxRows={16}
              fullWidth
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
              helperText={`${newContent.length.toLocaleString()} characters`}
            />
            <Box display="flex" justifyContent="flex-end" gap={1} mt={1.5}>
              <Button size="small" onClick={() => setShowNewVersion(false)}>Cancel</Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleCreateVersion}
                disabled={creating || !newContent.trim()}
              >
                {creating ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                Create Version
              </Button>
            </Box>
          </Box>
        )}

        {/* Version List */}
        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
        ) : versions.length === 0 ? (
          <Alert severity="info">No versions found for this prompt key.</Alert>
        ) : (
          <Box display="flex" flexDirection="column" gap={1.5}>
            {versions.map((v) => (
              <Box
                key={v.version}
                sx={{
                  border: '1px solid',
                  borderColor: v.isActive ? '#22c55e' : '#e0e0e0',
                  borderRadius: 2,
                  p: 2,
                  bgcolor: v.isActive ? '#f0fdf4' : '#fafafa',
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                  <Typography variant="subtitle2" fontWeight={600}>
                    v{v.version}
                  </Typography>
                  <Chip
                    label={v.isActive ? 'Active' : 'Inactive'}
                    color={v.isActive ? 'success' : 'default'}
                    size="small"
                  />
                  <Chip label={v.promptType?.replace(/_/g, ' ')} size="small" variant="outlined" />
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1, textAlign: 'right' }}>
                    {formatDate(v.createdAt)}
                    {v.createdBy && ` by ${v.createdBy}`}
                  </Typography>

                  {/* Expand/collapse */}
                  <Tooltip title={expandedVersion === v.version ? 'Collapse' : 'View content'}>
                    <IconButton
                      size="small"
                      onClick={() => setExpandedVersion(expandedVersion === v.version ? null : v.version)}
                    >
                      {expandedVersion === v.version ? <CollapseIcon /> : <ExpandIcon />}
                    </IconButton>
                  </Tooltip>

                  {/* Deactivate */}
                  {v.isActive && (
                    <Tooltip title="Deactivate this version">
                      <span>
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => handleDeactivate(v.version)}
                          disabled={deactivating === v.version}
                        >
                          {deactivating === v.version ? <CircularProgress size={16} /> : <DeactivateIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>

                {v.description && (
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    {v.description}
                  </Typography>
                )}

                {/* Expanded content */}
                {expandedVersion === v.version && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        maxHeight: 300,
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: 13,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {v.promptContent}
                    </Box>
                  </>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LLMPromptVersions;
